// Gedeelde basis voor de modules Vandaag, Offertes en Facturen, Klantdossier en Systeemstatus.
// Gebruikt dezelfde kleuren en klassen (card, btn, tab, ovl, mdl) als de rest van de OS.
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../supabase";

export const C = {
  bg: "#0a0a0c", surface: "#131418", surfaceHigh: "#1c1d23", border: "#2a2c34",
  green: "#34bd82", greenDim: "rgba(52,189,130,0.14)", red: "#e5564e", redDim: "rgba(229,86,78,0.14)",
  yellow: "#e0a82e", yellowDim: "rgba(224,168,46,0.14)", blue: "#5b8eff", blueDim: "rgba(91,142,255,0.14)",
  text: "#f3f4f6", muted: "#a0a4af", dim: "#4a4d57",
};

export const eur = (n) => "€ " + Number(n || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const eur0 = (n) => "€ " + Math.round(Number(n || 0)).toLocaleString("nl-NL");
export const afr = (n) => Math.round((Number(n) || 0) * 100) / 100;

const pad = (n) => String(n).padStart(2, "0");
export const isoVan = (d) => d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
export const vandaagIso = () => isoVan(new Date());
export const plusDagen = (iso, n) => { const d = new Date((iso || vandaagIso()) + "T12:00:00"); d.setDate(d.getDate() + n); return isoVan(d); };
export const dagenTot = (iso) => Math.round((new Date(iso + "T12:00:00") - new Date(vandaagIso() + "T12:00:00")) / 86400000);
export const datumNl = (iso) => (iso ? new Date(iso.length <= 10 ? iso + "T12:00:00" : iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" }) : "");
export const kwartaal = (iso) => "Q" + Math.ceil((new Date(iso + "T12:00:00").getMonth() + 1) / 3) + "-" + new Date(iso + "T12:00:00").getFullYear();

// Het streepje dat de CRM als lege bedrijfsnaam bewaart
const LEEG_STREEP = String.fromCharCode(8212);
export const isGewonnen = (l) => !!l && (l.status === "won" || l.status === "gewonnen");
export const isVerloren = (l) => !!l && (l.status === "lost" || l.status === "verloren");
export const echtBedrijf = (l) => (l && l.company && l.company !== LEEG_STREEP ? l.company : "");
export const leadNaam = (l) => (l ? (l.company && l.company !== LEEG_STREEP ? l.company : l.name) : "");

// Lees een query en geef altijd een lijst terug. Ontbreekt de tabel (migratie nog niet gedraaid),
// dan staat dat in `ontbreekt` zodat het scherm een duidelijke melding toont in plaats van te crashen.
export async function lees(query) {
  try {
    const r = await query;
    if (r.error) return { data: [], fout: r.error, ontbreekt: tabelOntbreekt(r.error) };
    return { data: r.data || [], fout: null, ontbreekt: false };
  } catch (e) {
    return { data: [], fout: e, ontbreekt: false };
  }
}
export function tabelOntbreekt(fout) {
  if (!fout) return false;
  const c = String(fout.code || "");
  return c === "42P01" || c === "42703" || c === "PGRST205" || c === "PGRST204" || c === "42883" || /does not exist|schema cache|Could not find/i.test(String(fout.message || ""));
}

async function metSessie(headers = {}) {
  const { data } = await supabase.auth.getSession();
  const token = data && data.session && data.session.access_token;
  return token ? { ...headers, Authorization: `Bearer ${token}` } : { ...headers };
}
export function apiFetch(url, opts = {}) {
  return metSessie(opts.headers || {}).then((headers) => fetch(url, { ...opts, headers }));
}

export function Portal({ children }) { return createPortal(children, document.body); }

// Eén uniforme paginakop voor elke module: titel in gewone letters, korte uitleg, acties rechts.
export const KOP_STIJL = { fontSize: 19, fontWeight: 700, letterSpacing: "-.01em", margin: 0, fontFamily: "inherit" };
export function Kop({ titel, sub, rechts }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
      <div style={{ minWidth: 0 }}>
        <h1 style={KOP_STIJL}>{titel}</h1>
        {sub && <p style={{ fontSize: 12, color: C.muted, margin: "3px 0 0", lineHeight: 1.45 }}>{sub}</p>}
      </div>
      {rechts && <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>{rechts}</div>}
    </div>
  );
}

// Rij compacte tellers bovenaan een module: [{ label, waarde, kleur, onClick }]
export function Tellers({ items }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
      {items.map((t) => (
        <div key={t.label} onClick={t.onClick} style={{ flex: 1, minWidth: 0, background: C.surface, border: "1px solid " + C.border, borderRadius: 8, padding: "8px 10px", cursor: t.onClick ? "pointer" : "default" }}>
          <p className="lbl" style={{ margin: 0, fontSize: 9, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.label}</p>
          <p style={{ fontFamily: "monospace", fontSize: 18, fontWeight: 700, color: t.kleur || C.text, margin: "2px 0 0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.waarde}</p>
        </div>
      ))}
    </div>
  );
}

// Eén kaart met een kop (kleurstreepje, naam, aantal) en daaronder de rijen. Klik op de kop om in of uit te klappen.
export function Groep({ label, kleur, aantal, standaardDicht, voet, children }) {
  const [dicht, setDicht] = useState(!!standaardDicht);
  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }}>
      <div onClick={() => setDicht((d) => !d)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", cursor: "pointer", userSelect: "none" }}>
        <span style={{ width: 3, height: 14, borderRadius: 2, background: kleur || C.muted }} />
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase" }}>{label}</span>
        {aantal != null && <span style={{ fontSize: 11, color: C.muted, fontFamily: "monospace" }}>{aantal}</span>}
        <span style={{ marginLeft: "auto", fontSize: 11, color: C.dim }}>{dicht ? "▸" : "▾"}</span>
      </div>
      {!dicht && children}
      {!dicht && voet}
    </div>
  );
}

// Eén compacte regel (titel, regel met kleine gegevens, eventueel een korte voorvertoning). Klik klapt de details open.
// `voor` = bijv. een vinkje of stip, `rechts` = bijv. een status, kinderen = de details.
export function Rij({ voor, titel, meta, voorbeeld, rechts, gedimd, standaardOpen, kleur, onKlik, children }) {
  const [open, setOpen] = useState(!!standaardOpen);
  const heeftDetails = !onKlik && children != null && children !== false;
  return (
    <div style={{ borderTop: "1px solid " + C.border, opacity: gedimd ? 0.6 : 1 }}>
      <div onClick={() => (onKlik ? onKlik() : heeftDetails && setOpen((o) => !o))} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", cursor: heeftDetails || onKlik ? "pointer" : "default" }}>
        {voor}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            {kleur && <span style={{ width: 8, height: 8, borderRadius: "50%", background: kleur, flexShrink: 0, marginTop: 5 }} />}
            <span style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4, overflowWrap: "anywhere", textDecoration: gedimd ? "line-through" : "none", color: gedimd ? C.muted : C.text, ...(open ? {} : { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }) }}>{titel}</span>
          </div>
          {meta && <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 4, paddingLeft: kleur ? 16 : 0, fontSize: 11, color: C.muted }}>{meta}</div>}
          {!open && voorbeeld && <p style={{ margin: "5px 0 0", paddingLeft: kleur ? 16 : 0, fontSize: 12, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{voorbeeld}</p>}
        </div>
        {rechts}
        {heeftDetails && <span aria-hidden="true" style={{ color: C.dim, fontSize: 12, marginTop: 2, flexShrink: 0 }}>{open ? "▴" : "▾"}</span>}
        {onKlik && <span aria-hidden="true" style={{ color: C.dim, fontSize: 16, lineHeight: 1, marginTop: 1, flexShrink: 0 }}>›</span>}
      </div>
      {open && heeftDetails && <div style={{ padding: "0 12px 12px " + (voor ? 40 : 12) + "px" }}>{children}</div>}
    </div>
  );
}

export function Leeg({ tekst }) {
  return <div style={{ padding: 22, textAlign: "center", color: C.muted, fontSize: 13 }}>{tekst}</div>;
}

export function Stat({ label, waarde, kleur, sub, onClick }) {
  return (
    <div className="card" onClick={onClick} style={{ padding: "10px 12px", cursor: onClick ? "pointer" : "default", minWidth: 0 }}>
      <p className="lbl" style={{ marginBottom: 4 }}>{label}</p>
      <p style={{ fontSize: 18, fontWeight: 700, color: kleur || C.text, margin: 0, wordBreak: "break-word" }}>{waarde}</p>
      {sub && <p style={{ fontSize: 11, color: C.muted, margin: "4px 0 0" }}>{sub}</p>}
    </div>
  );
}

export function Chip({ tekst, kleur }) {
  const k = kleur || C.muted;
  const bg = k === C.green ? C.greenDim : k === C.red ? C.redDim : k === C.yellow ? C.yellowDim : k === C.blue ? C.blueDim : "rgba(160,164,175,0.12)";
  return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: bg, color: k, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>{tekst}</span>;
}

export function Melding({ type = "info", children }) {
  const k = type === "fout" ? C.red : type === "ok" ? C.green : type === "let-op" ? C.yellow : C.blue;
  const bg = type === "fout" ? C.redDim : type === "ok" ? C.greenDim : type === "let-op" ? C.yellowDim : C.blueDim;
  return <div style={{ background: bg, border: "1px solid " + k + "55", color: C.text, borderRadius: 8, padding: "10px 14px", fontSize: 12, lineHeight: 1.55, marginBottom: 12 }}>{children}</div>;
}

export function MigratieMelding() {
  return (
    <Melding type="let-op">
      <strong>Eenmalige database-stap nog nodig.</strong> Plak het bestand <code>supabase/migrations/os_optimalisatie.sql</code> in de Supabase SQL-editor en voer het uit.
      Daarna werkt dit scherm volledig. Het bestand is veilig om opnieuw uit te voeren en verwijdert niets.
    </Melding>
  );
}

export function Venster({ titel, onSluit, breed, children }) {
  return (
    <Portal>
      <div className="ovl" onClick={onSluit}>
        <div className="mdl" style={{ maxWidth: breed || 560 }} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 10 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{titel}</h2>
            <button className="btn bg" onClick={onSluit} aria-label="Sluiten">Sluiten</button>
          </div>
          {children}
        </div>
      </div>
    </Portal>
  );
}

export function Veld({ label, children, hulp }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label className="lbl" style={{ display: "block", marginBottom: 5 }}>{label}</label>
      {children}
      {hulp && <p style={{ fontSize: 11, color: C.muted, margin: "4px 0 0" }}>{hulp}</p>}
    </div>
  );
}

// Regels: omschrijving, aantal, prijs ex btw. Regels met maand=true zijn doorlopend (per maand) en tellen niet mee in het eenmalige totaal.
export const regelTotaal = (regels) => afr((regels || []).filter((r) => !r.maand).reduce((s, r) => s + (Number(r.aantal) || 0) * (Number(r.prijs) || 0), 0));
export const maandTotaal = (regels) => afr((regels || []).filter((r) => r.maand).reduce((s, r) => s + (Number(r.aantal) || 0) * (Number(r.prijs) || 0), 0));
export function berekenTotalen(regels, btwPct) {
  const excl = regelTotaal(regels);
  const btw = afr(excl * (Number(btwPct) || 0) / 100);
  return { excl, btw, incl: afr(excl + btw), maand: maandTotaal(regels) };
}

// Printbare weergave van een offerte of factuur. Opent een nieuw venster; de gebruiker kiest zelf "Bewaar als PDF".
export function openAfdruk({ soort, nummer, datum, geldigTot, vervalt, klant, titel, regels, btwPct, notities, betaalUrl }) {
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const t = berekenTotalen(regels, btwPct);
  const rijen = (regels || []).filter((r) => !r.maand).map((r) => `<tr><td>${esc(r.omschrijving)}</td><td class="r">${esc(r.aantal)}</td><td class="r">${esc(eur(r.prijs))}</td><td class="r">${esc(eur((Number(r.aantal) || 0) * (Number(r.prijs) || 0)))}</td></tr>`).join("");
  const maandRijen = (regels || []).filter((r) => r.maand).map((r) => `<tr><td>${esc(r.omschrijving)}</td><td class="r">${esc(r.aantal)}</td><td class="r">${esc(eur(r.prijs))}</td><td class="r">${esc(eur((Number(r.aantal) || 0) * (Number(r.prijs) || 0)))}</td></tr>`).join("");
  const w = window.open("", "_blank");
  if (!w) return false;
  const veilig = betaalUrl && /^https:\/\//.test(betaalUrl) ? betaalUrl : "";
  w.document.write(`<!doctype html><html lang="nl"><head><meta charset="utf-8"><title>${esc(soort)} ${esc(nummer)}</title>
<style>body{font-family:Helvetica,Arial,sans-serif;color:#111;max-width:760px;margin:40px auto;padding:0 24px;font-size:14px}
h1{font-size:26px;margin:0 0 4px}.m{color:#555;font-size:13px}table{width:100%;border-collapse:collapse;margin:24px 0}
th{text-align:left;border-bottom:2px solid #111;padding:8px 6px;font-size:12px;text-transform:uppercase}td{padding:8px 6px;border-bottom:1px solid #ddd}
.r{text-align:right}.t td{border:none;padding:3px 6px}.b{font-weight:700;font-size:16px}.k{display:flex;justify-content:space-between;gap:24px;margin-top:24px}
.pay{margin-top:24px;padding:12px;border:1px solid #111;border-radius:6px}@media print{.np{display:none}}</style></head><body>
<p class="np"><button onclick="window.print()">Afdrukken of bewaar als PDF</button></p>
<h1>${esc(soort)}</h1><p class="m">${esc(nummer)} &middot; ${esc(datum)}${geldigTot ? " &middot; geldig tot " + esc(geldigTot) : ""}${vervalt ? " &middot; betalen v&oacute;&oacute;r " + esc(vervalt) : ""}</p>
<div class="k"><div><strong>Dif3r3nt VOF</strong><br>info@dif3r3nt.nl<br>dif3r3nt.nl</div><div><strong>${esc(klant.naam)}</strong>${klant.bedrijf ? "<br>" + esc(klant.bedrijf) : ""}${klant.email ? "<br>" + esc(klant.email) : ""}</div></div>
<p style="margin-top:28px"><strong>${esc(titel)}</strong></p>
<table><thead><tr><th>Omschrijving</th><th class="r">Aantal</th><th class="r">Prijs</th><th class="r">Totaal</th></tr></thead><tbody>${rijen}</tbody></table>
${maandRijen ? `<table><thead><tr><th>Per maand (doorlopend)</th><th class="r">Aantal</th><th class="r">Prijs</th><th class="r">Totaal</th></tr></thead><tbody>${maandRijen}</tbody></table>` : ""}
<table class="t" style="width:280px;margin-left:auto"><tr><td>Subtotaal</td><td class="r">${esc(eur(t.excl))}</td></tr><tr><td>BTW ${esc(btwPct)}%</td><td class="r">${esc(eur(t.btw))}</td></tr><tr class="b"><td>Totaal</td><td class="r">${esc(eur(t.incl))}</td></tr></table>
${notities ? `<p class="m">${esc(notities)}</p>` : ""}
${veilig ? `<div class="pay">Direct betalen met iDEAL of creditcard: <a href="${esc(veilig)}">${esc(veilig)}</a></div>` : ""}
</body></html>`);
  w.document.close();
  return true;
}

// Keuzelijst met leads/klanten, om een afspraak, document of taak aan een klant te hangen (lead_id).
// Toont niets als de leads niet geladen kunnen worden, zodat het formulier eromheen blijft werken.
export function LeadKiezer({ waarde, onChange, label = "Klant (optioneel)" }) {
  const [leads, setLeads] = useState([]);
  useEffect(() => {
    let weg = false;
    lees(supabase.from("leads").select("id,name,company").order("created_at", { ascending: false }).limit(500)).then((r) => { if (!weg) setLeads(r.data); });
    return () => { weg = true; };
  }, []);
  if (!leads.length) return null;
  return (
    <div>
      <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>{label}</p>
      <select value={waarde || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">Geen</option>
        {leads.map((l) => <option key={l.id} value={l.id}>{leadNaam(l)}</option>)}
      </select>
    </div>
  );
}

// Vraag Claude via de bestaande edge function claude-chat (dag-budget en inlog van een teamlid gelden daar).
export async function vraagClaude(system, gebruiker, { maxTokens = 700 } = {}) {
  const base = process.env.REACT_APP_SUPABASE_URL;
  const anon = process.env.REACT_APP_SUPABASE_ANON_KEY;
  if (!base || !anon) throw new Error("Geen verbinding met de AI-functie ingesteld");
  const r = await fetch(`${base}/functions/v1/claude-chat`, {
    method: "POST",
    headers: await metSessie({ "Content-Type": "application/json", apikey: anon }),
    body: JSON.stringify({ model: "claude-sonnet-4-6", temperature: 0.3, max_completion_tokens: maxTokens, messages: [{ role: "system", content: system }, { role: "user", content: gebruiker }] }),
  });
  if (!r.ok) throw new Error(r.status === 429 ? "Het AI-dagbudget is op" : "AI niet bereikbaar (" + r.status + ")");
  const d = await r.json();
  if (d.error) throw new Error(d.error.message || "AI-fout");
  return (d.choices && d.choices[0] && d.choices[0].message && d.choices[0].message.content) || "";
}

// Markeringen die de database in de notitie van een taak zet om taken terug te vinden. Die zijn voor de machine, niet voor jou.
export const TECHNISCHE_NOTITIE = /^(oplevering|factuur|offerte):[0-9a-f-]+$/i;
export const schoneNotitie = (n) => (n && !TECHNISCHE_NOTITIE.test(String(n).trim()) ? String(n) : "");
